// The state behind one DeleteResourceDialog: which resource is up for deletion,
// what the backend's preview says the deletion will take with it, whether the
// execute is in flight, and whether the scope moved under the user.
//
// Two guarantees the call sites used to get wrong on their own:
//
// - Only the preview for the currently open target is ever shown. Every open
//   and close bumps a request counter, and a preview that answers an older
//   request is dropped — so cancelling A while its check loads and opening B
//   can no longer put A's dependents under B's title (H74).
// - A confirmation is only ever executed against the scope the user reviewed.
//   The execute carries the preview's fingerprint; when the backend finds the
//   dependents have changed it refuses, and the dialog stays open showing the
//   fresh scope for a fresh confirmation (M228).
import { useCallback, useRef, useState } from 'react';
import { executeResourceDelete, previewResourceDelete } from '../../services/resourceActions';

export default function useDeleteConfirmation({
  resourceType,
  extraFor = () => ({}),      // request fields beyond the id (a value's category_id)
  onPreviewError,             // (error, target) — the dependency check failed; the dialog closes
  onDeleteError,              // (error, target) — the delete failed for a reason other than scope or blocking
  onDeleted,                  // (target) — the delete committed
}) {
  const [target, setTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scopeChanged, setScopeChanged] = useState(false);
  const [executionError, setExecutionError] = useState(null);
  const requestRef = useRef(0);

  const open = useCallback(async (nextTarget) => {
    requestRef.current += 1;
    const request = requestRef.current;
    setTarget(nextTarget);
    setPreview(null);
    setScopeChanged(false);
    setExecutionError(null);
    let result;
    try {
      result = await previewResourceDelete(resourceType, nextTarget.id, extraFor(nextTarget));
    } catch (error) {
      if (requestRef.current !== request) return;   // closed, or reopened for another target
      setTarget(null);
      onPreviewError?.(error, nextTarget);
      return;
    }
    if (requestRef.current !== request) return;
    setPreview(result);
  }, [resourceType, extraFor, onPreviewError]);

  const close = useCallback(() => {
    if (busy) return;
    requestRef.current += 1;
    setTarget(null);
    setPreview(null);
    setScopeChanged(false);
    setExecutionError(null);
  }, [busy]);

  const confirm = useCallback(async () => {
    if (!target) return;
    setBusy(true);
    setExecutionError(null);
    try {
      await executeResourceDelete(resourceType, target.id, preview, extraFor(target));
    } catch (error) {
      if (error.scopeChanged) {
        setPreview(error.preview);
        setScopeChanged(true);
      } else if (error.blocked) {
        setPreview({ blocking: true, dependents: error.dependents });
      } else {
        setExecutionError(error.message || 'Could not complete deletion. Please try again.');
        onDeleteError?.(error, target);
      }
      setBusy(false);
      return;
    }
    const deleted = target;
    requestRef.current += 1;
    setTarget(null);
    setPreview(null);
    setScopeChanged(false);
    setExecutionError(null);
    setBusy(false);
    onDeleted?.(deleted);
  }, [resourceType, target, preview, extraFor, onDeleteError, onDeleted]);

  return { target, preview, busy, scopeChanged, executionError, open, close, confirm };
}
